import { queryOptions } from "@tanstack/react-query"

import {
  type AuthStrategy,
  type EditorConfig,
  throwConfigError,
} from "../../EditorConfig"
import {
  type DocumentVersion,
  getDocumentList,
  makeSearchFullTextDocumentsQueryString,
  searchFullTextDocuments,
} from "./document"
import type { Repository } from "./repository"

interface SearchDocumentsArgs {
  coreApiBaseUrl: URL
  documentSearchBaseUrl: URL
  authStrategy: AuthStrategy
  repository: string
  searchTerm?: string
  customTypes?: string[]
  tags?: string[]
  authors?: string[]
  status?: DocumentVersion["status"][]
  locale?: string
  /**
   * TODO: Remove after all repos have been migrated to storageVerison 7 or above (PBD-1908)
   */
  storageVersion?: Repository["storageVersion"]
  track:
    | ((args: {
        numberOfResults: number
        searchQuery: string
        numberOfWords: number
        timeToGetResultsInMs: number
      }) => void)
    | undefined
  signal: AbortSignal
}

export async function searchDocuments(args: SearchDocumentsArgs) {
  const {
    coreApiBaseUrl,
    documentSearchBaseUrl,
    authStrategy,
    repository,
    searchTerm = "",
    customTypes,
    tags,
    authors,
    status,
    locale,
    track,
    signal,
    storageVersion,
  } = args

  // TODO: remove this after all repos are migrated to the new storage version (PBD-1908)
  // if the storage version is less than 7, we need to use the old search endpoint
  // not all repos were migrated to the new storage version, which writes to OpenSearch
  if (storageVersion && storageVersion.major <= 6) {
    // since we removed pagination and the old search doesn't provide great results
    // we define a high limit to attempt to get more relevant results
    const limit = 200
    const statuses: DocumentVersion["status"][] = status?.length
      ? status
      : ["published", "unclassified", "release"]

    const documents = await getDocumentList({
      baseUrl: coreApiBaseUrl,
      authStrategy,
      tags,
      customTypes,
      language: locale,
      term: searchTerm,
      authors,
      statuses,
      limit,
    })

    return {
      results: documents.results,
    }
  }

  const queryString = makeSearchFullTextDocumentsQueryString({
    repository,
    searchTerm,
    tags,
    authors,
    status,
    localeId: locale,
    customTypesIds: customTypes,
  })

  const startTime = performance.now()

  const response = await searchFullTextDocuments(
    documentSearchBaseUrl,
    authStrategy,
    queryString,
    { signal },
  )

  const documents = response.results

  const endTime = performance.now()
  const duration = endTime - startTime
  track?.({
    numberOfResults: documents.length,
    searchQuery: searchTerm,
    numberOfWords: searchTerm.split(" ").length,
    timeToGetResultsInMs: duration,
  })

  return {
    results: documents,
  }
}

interface SearchDocumentsQueryOptionsArgs {
  searchTerm?: string
  customTypes?: string[]
  tags?: string[]
  authors?: string[]
  status?: DocumentVersion["status"][]
  locale?: string
  /**
   * TODO: Remove after all repos have been migrated to storageVerison 7 or above (PBD-1908)
   */
  storageVersion?: Repository["storageVersion"]
  config: SearchDocumentsConfig
  track?: (args: {
    numberOfResults: number
    searchQuery: string
    numberOfWords: number
    timeToGetResultsInMs: number
  }) => void
}

export function searchDocumentsQueryOptions(
  args: SearchDocumentsQueryOptionsArgs,
) {
  const {
    searchTerm,
    customTypes,
    tags,
    authors,
    status,
    locale,
    config,
    track,
    storageVersion,
  } = args

  if (!config.searchDocuments) throwConfigError("searchDocuments")

  const { coreApiBaseUrl, documentSearchBaseUrl, authStrategy, repository } =
    config

  return queryOptions({
    queryKey: [
      "searchDocuments",
      {
        coreApiBaseUrl,
        documentSearchBaseUrl,
        authStrategy,
        repository,
        searchTerm,
        customTypes,
        tags,
        authors,
        status,
        locale,
        track,
        storageVersion,
      },
    ] as const,
    queryFn: ({ queryKey: [, searchArgs], signal }) =>
      searchDocuments({ ...searchArgs, signal }),
  })
}

type SearchDocumentsConfig = Pick<
  EditorConfig & { searchDocuments: true },
  | "authStrategy"
  | "coreApiBaseUrl"
  | "documentSearchBaseUrl"
  | "repository"
  | "searchDocuments"
>
